<script>
	// NPM Modules
	import collect from 'collect.js';
	// Components
	import CheckBox from '@/Components/CheckBox.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import DragDropList from '@/Components/DragDropList.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';

	export let sport;
	export let competitions;

	let loading = false;
	let competitionsInSport = collect(competitions);
	let featuredCompetitions = getFeatureCompetitions(competitionsInSport); //Feature comps but still in a collection!
	let simpleFeaturedCompetitions = generateSimpleFeaturedList(competitionsInSport); //This needs to be an unreactive basic array or the magic stops!

	$: simpleFeaturedCompetitions && updateFeatureCompetitionsFromList(simpleFeaturedCompetitions); //if the magic list is updated push the changes to the collection list.
	$: competitionChanged(competitionsInSport); //Hook to change if the sport is changed and update the non-reactive lists

	function competitionChanged(competitions) {
		featuredCompetitions = getFeatureCompetitions(competitions);
		simpleFeaturedCompetitions = generateSimpleFeaturedList(competitions);
	}

	/**
	 * pulls the featured comps from the sport and re-indexs them
	 */
	function getFeatureCompetitions(competitions) {
		let counter = 1;
		return competitions
			.sortBy('name')
			.filter((competition) => {
				return competition.featured;
			})
			.sortBy('featured_order')
			.transform((competition) => {
				competition.featured_order = counter++;
				return competition;
			});
	}

	/**
	 * Function simplifies the collections to basic arrays so that the DragDrop component can do it magic!
	 */
	function generateSimpleFeaturedList(competitions) {
		return competitions
			.sortBy('name')
			.filter((competition) => {
				return competition.featured;
			})
			.sortBy('featured_order')
			.transform((competition) => {
				return {
					id: competition.featured_order,
					html: '<div class="flex items-center"><div class="mr-2 font-semibold">' + competition.name + '</div></div>',
					competitionId: competition.id
				};
			})
			.all();
	}

	/**
	 * Takes the magic list and processes changes back to the collection!
	 */
	function updateFeatureCompetitionsFromList(simplifiedFeaturedCompetitions) {
		if (typeof simplifiedFeaturedCompetitions === 'undefined') {
			return [];
		}
		let removeCompId = [];
		featuredCompetitions = featuredCompetitions
			.transform((competition) => {
				let found = false;
				for (let i = 0; i < simplifiedFeaturedCompetitions.length; i++) {
					if (competition.id === simplifiedFeaturedCompetitions[i].competitionId) {
						competition.featured_order = i + 1; //Here we reorder everything based on the array key index (should always been in order)
						found = true;
					}
				}
				if (!found) {
					//If the comp was removed by clicking the X we need to filter it out
					removeCompId.push(competition.id);
				}
				return competition;
			})
			.filter((competition) => {
				return competition.featured;
			})
			.sortBy('featured_order');

		for (let i = 0; i < removeCompId.length; i++) {
			//If the comp was removed by clicking the X we need to filter it out
			featureCompetition(removeCompId[i], false);
		}
	}

	/**
	 * Logic for the checkbox and enable/disable the features!
	 */
	function featureCompetition(competitionId, checked) {
		const currentHighest = parseInt(competitionsInSport.max('featured_order')); //Get the highest order number so we are always putting a new item at the end
		competitionsInSport = competitionsInSport.transform((competition) => {
			if (competition.id === competitionId) {
				if (checked) {
					competition.featured = true;
					competition.featured_order = currentHighest + 1;
				} else {
					competition.featured = false;
					competition.featured_order = null;
				}

				competition
					.patch({
						featured: competition.featured,
						featured_order: competition.featured_order
					})
					.then(() => {
						notify({
							type: 'success',
							title: 'Featured competitions',
							message: competition.featured
								? 'Successfully added as featured competition'
								: 'Successfully removed as featured competition',
							timeout: 3
						});
					});
			}
			return competition;
		});
		competitionsInSport = competitionsInSport.sortBy('name');
		featuredCompetitions = getFeatureCompetitions(competitionsInSport);
		simpleFeaturedCompetitions = generateSimpleFeaturedList(competitionsInSport);
	}

	async function saveFeaturedCompetitionsOrder() {
		try {
			loading = true;
			await featuredCompetitions.each((competition) => {
				const competitionData = {
					featured: competition.featured,
					featured_order: competition.featured_order
				};
				competition.patch(competitionData);
			});
			notify({
				type: 'success',
				title: 'Featured competitions',
				message: 'Featured competitions order successfully saved',
				timeout: 3
			});
		} catch (err) {
			notify({
				type: 'danger',
				title: 'Featured competitions',
				message: 'Problem occurred saving competition order!',
				timeout: 3
			});
		}
		loading = false;
	}
</script>

{#if sport}
	<div class="w-full flex justify-end">
		<LoadingButton on:click={() => saveFeaturedCompetitionsOrder()} className="btn btn-primary" {loading}>
			Save Featured Competitions
		</LoadingButton>
	</div>
	<div class="overflow-x-auto rounded">
		<table class="w-full whitespace-nowrap mt-3">
			<thead>
				<tr class="border-t-2 border-b-2">
					<th colspan="3">
						<h1 class="text-xl font-bold mt-3">Featured Competitions</h1>
						<p class="font-light">(Drag to re-order)</p>
					</th>
				</tr>
			</thead>
			<tbody class="bg-white">
				{#if featuredCompetitions && featuredCompetitions.count() > 0}
					<tr>
						<td colspan="3" class="bg-gray-100 py-3">
							<DragDropList bind:data={simpleFeaturedCompetitions} removesItems={true} />
						</td>
					</tr>
				{:else}
					<tr>
						<td class="px-6 py-4 text-center border-t" colspan="3">
							No featured competitions found for {sport.name ?? ''}.
						</td>
					</tr>
				{/if}
			</tbody>
			<thead>
				<tr class="border-t-2 border-b-2">
					<th colspan="3"><h1 class="text-xl font-bold my-3">Competitions</h1></th>
				</tr>
				<tr class="text-xs text-left text-gray-500 uppercase">
					<th class="px-6 pt-3 pb-2 font-medium">Competition</th>
					<th class="w-16 px-6 pt-3 pb-2 font-medium">Sport</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium">Featured</th>
				</tr>
			</thead>
			<tbody class="bg-white">
				{#each competitionsInSport.all() as competition (competition.id)}
					<tr class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100">
						<td class="p-4 px-6 border-t">
							<div class="flex items-center">
								<div class="mr-2 font-semibold">{competition.name}</div>
							</div>
						</td>
						<td class="p-4 px-6 border-t">
							<div class="px-2 py-1 bg-green-500 rounded-md text-light">
								{sport.name}
							</div>
						</td>
						<td class="w-px px-6 border-t">
							<CheckBox
								on:change={(e) => featureCompetition(competition.id, e.target.checked)}
								bind:checked={competition.featured}
							/>
						</td>
					</tr>
				{:else}
					<tr>
						<td class="px-6 py-4 text-center border-t" colspan="3">
							No competitions found for {sport.name ?? ''}.
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</div>
{/if}
